import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useApi } from "@/hooks/useApi";
import { safeLocalStorage } from "@/lib/storage";
import { REVIEW_MODEL_MODES, REVIEW_TRIGGERS, normalizeReviewTrigger, type ReviewModelMode, type ReviewTrigger } from "@/hooks/useUserReviewSettings";
import { PROOF_TRIGGERS, type ProofTrigger } from "@/hooks/useUserProofSettings";
import { DREAMING_TRIGGERS, type DreamingTrigger } from "@/hooks/useUserDreamingSettings";
import * as z from "zod";
import { toast } from "sonner";
const RepositorySchema = z.object({
  id: z.string(),
  name: z.string(),
  indexing_enabled: z.boolean()
});
const ModelPresetInfoSchema = z.object({
  name: z.string(),
  models: z.record(z.string(), z.string()).optional()
});
export const CODE_REVIEW_MODES = ["disabled", "opt_in", "opt_out", "required"] as const;
export type CodeReviewMode = (typeof CODE_REVIEW_MODES)[number];
export const DREAMING_MODES = ["disabled", "opt_in", "opt_out", "required"] as const;
export type DreamingMode = (typeof DREAMING_MODES)[number];
export const PROOF_MODES = ["disabled", "opt_in", "opt_out", "required"] as const;
export type ProofMode = (typeof PROOF_MODES)[number];
export const PROOF_PUBLISH = ["none", "description", "comment"] as const;
export type ProofPublish = (typeof PROOF_PUBLISH)[number];
const CodesmithSettingsSchema = z.object({
  codesmith_enabled: z.boolean(),
  repositories: z.record(z.string(), RepositorySchema),
  auto_iterate_ci_default: z.boolean().optional().default(true),
  auto_respond_reviews_default: z.enum(["all", "human", "bots", "none"]).optional().default("bots"),
  auto_resolve_conflicts_default: z.boolean().optional().default(true),
  codesmith_pr_footer_enabled: z.boolean().optional().default(true),
  codesmith_check_run_enabled: z.boolean().optional().default(true),
  codesmith_slack_thread_mode: z.enum(["separate", "inline"]).optional().default("inline"),
  codesmith_memories_allowed: z.boolean().optional().default(true),
  codesmith_code_review: z.enum(CODE_REVIEW_MODES).optional().default("disabled"),
  codesmith_code_review_trigger: z.enum([...REVIEW_TRIGGERS, "as_needed"] as const).transform(normalizeReviewTrigger).optional().default("push"),
  codesmith_code_review_model_mode: z.enum(REVIEW_MODEL_MODES).optional().default("fast"),
  codesmith_code_review_publish: z.boolean().optional().default(false),
  codesmith_dreaming: z.enum(DREAMING_MODES).optional().default("disabled"),
  codesmith_dreaming_trigger: z.enum(DREAMING_TRIGGERS).optional().default("pr_closed"),
  codesmith_proof: z.enum(PROOF_MODES).optional().default("disabled"),
  codesmith_proof_trigger: z.enum(PROOF_TRIGGERS).optional().default("opened"),
  codesmith_proof_max_rounds: z.number().int().min(1).max(10).optional().default(3),
  codesmith_proof_publish: z.enum(PROOF_PUBLISH).optional().default("none"),
  codesmith_zdr_opted_out: z.boolean().optional().default(false),
  codesmith_diff_viewer_enabled: z.boolean().optional().default(false),
  codesmith_left_sidebar_redesign_enabled: z.boolean().optional().default(false),
  codesmith_session_search_enabled: z.boolean().optional().default(false),
  // Where the switcher lists Fusion: with the official modes once GA, under
  // Internal for superusers before that, or not at all.
  codesmith_fusion_placement: z.enum(["official", "internal", "hidden"]).optional().default("hidden"),
  // Retention arm -> official mode -> preset metadata (human name + per-agent
  // model breakdown) resolved from the live rollouts; superusers only.
  codesmith_model_modes_by_arm: z.partialRecord(z.enum(["zdr", "zdr_opt_out"]), z.record(z.string(), ModelPresetInfoSchema)).optional().default({}),
  // Internal catalog presets, keyed by preset; superusers only.
  codesmith_internal_model_presets: z.record(z.string(), ModelPresetInfoSchema.extend({
    label: z.string()
  })).optional().default({}),
  // The org's data-retention arm; only superusers receive it.
  codesmith_retention_arm: z.enum(["zdr", "zdr_opt_out"]).nullable().optional().default(null)
});
export type CodesmithSettings = z.infer<typeof CodesmithSettingsSchema>;
function getCachedCodesmithSettings(org: string): CodesmithSettings | undefined {
  const cached = safeLocalStorage.get(`blacksmith_codesmith_settings_${org}`);
  if (!cached) return undefined;
  try {
    return CodesmithSettingsSchema.parse(JSON.parse(cached));
  } catch {
    return undefined;
  }
}
function setCachedCodesmithSettings(org: string, settings: CodesmithSettings) {
  safeLocalStorage.set(`blacksmith_codesmith_settings_${org}`, JSON.stringify(settings));
}

/**
 * Seed for an optimistic update that lands before the first fetch resolves,
 * e.g. `{ codesmith_pr_footer_enabled: false }` → every other setting at its
 * backend default.
 */
function optimisticSettings(overrides: Partial<CodesmithSettings>): CodesmithSettings {
  return {
    codesmith_enabled: true,
    repositories: {},
    auto_iterate_ci_default: true,
    auto_respond_reviews_default: "bots",
    auto_resolve_conflicts_default: true,
    codesmith_pr_footer_enabled: true,
    codesmith_check_run_enabled: true,
    codesmith_slack_thread_mode: "inline",
    codesmith_memories_allowed: true,
    codesmith_code_review: "disabled",
    codesmith_code_review_trigger: "push",
    codesmith_code_review_model_mode: "fast",
    codesmith_code_review_publish: false,
    codesmith_dreaming: "disabled",
    codesmith_dreaming_trigger: "pr_closed",
    codesmith_proof: "disabled",
    codesmith_proof_trigger: "opened",
    codesmith_proof_max_rounds: 3,
    codesmith_proof_publish: "none",
    codesmith_zdr_opted_out: false,
    codesmith_diff_viewer_enabled: false,
    codesmith_left_sidebar_redesign_enabled: false,
    codesmith_session_search_enabled: false,
    codesmith_fusion_placement: "hidden",
    codesmith_model_modes_by_arm: {},
    codesmith_internal_model_presets: {},
    codesmith_retention_arm: null,
    ...overrides
  };
}

/**
 * Org-aware variant that does NOT call `useActiveGithubOrgForUser`. Use this
 * from components that already have the active org from another source (e.g.
 * the URL pathname) and need to avoid the suspense triggered by
 * `useActiveGithubOrgForUser`. Shares the cache key, queryFn, zod parsing, and
 * localStorage sync with `useCodesmithSettings` so consumers of the shared
 * cache always see consistent data.
 */
export function useCodesmithSettingsForOrg(org) {
  const $ = _c(10);
  const api = useApi();
  let t0;
  if ($[0] !== org) {
    t0 = ["codesmith-settings", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api || $[3] !== org) {
    t1 = async () => {
      const response = await api.get(`/user/github/orgs/${org}/codesmith-settings`);
      const parsed = CodesmithSettingsSchema.parse(response.data);
      if (org) {
        setCachedCodesmithSettings(org, parsed);
      }
      return parsed;
    };
    $[2] = api;
    $[3] = org;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!org;
  let t3;
  if ($[5] !== org) {
    t3 = org ? getCachedCodesmithSettings(org) : undefined;
    $[5] = org;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = {
      enabled: t2,
      initialData: t3
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t0, t1, t4);
}
export function useCodesmithSettings() {
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  return useCodesmithSettingsForOrg(activeGithubOrg);
}

/**
 * An org's diff viewer flag, known only once a fetch has succeeded: a
 * localStorage seed may predate the field and parse to the schema default.
 */
export function useCodesmithDiffViewerEnabledForOrg(org) {
  const $ = _c(3);
  const {
    data,
    isFetched,
    isSuccess
  } = useCodesmithSettingsForOrg(org);
  const t0 = data?.codesmith_diff_viewer_enabled ?? false;
  const t1 = isFetched && isSuccess;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      enabled: t0,
      enabledKnown: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function useCodesmithLeftSidebarRedesignEnabledForOrg(org) {
  const $ = _c(3);
  const {
    data,
    isFetched,
    isSuccess
  } = useCodesmithSettingsForOrg(org);
  const t0 = data?.codesmith_left_sidebar_redesign_enabled ?? false;
  const t1 = isFetched && isSuccess;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      enabled: t0,
      enabledKnown: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function useCodesmithSessionSearchEnabledForOrg(org) {
  const $ = _c(3);
  const {
    data,
    isFetched,
    isSuccess
  } = useCodesmithSettingsForOrg(org);
  const t0 = data?.codesmith_session_search_enabled ?? false;
  const t1 = isFetched && isSuccess;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      enabled: t0,
      enabledKnown: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function useUpdateRepoIndexing() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async repositories => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/repo-indexing`, {
        repositories
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async repositories_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({});
        }
        const updatedRepositories = {
          ...old.repositories
        };
        Object.entries(repositories_0).forEach(t4 => {
          const [id, enabled] = t4;
          if (updatedRepositories[id]) {
            updatedRepositories[id] = {
              ...updatedRepositories[id],
              indexing_enabled: enabled
            };
          }
        });
        return {
          ...old,
          repositories: updatedRepositories
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _repositories, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update repository indexing settings");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
export function useUpdatePrFooterEnabled() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async enabled => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/pr-footer-enabled`, {
        enabled
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async enabled_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({
            codesmith_pr_footer_enabled: enabled_0
          });
        }
        return {
          ...old,
          codesmith_pr_footer_enabled: enabled_0
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _enabled, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update PR description comment setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
export function useUpdateSlackThreadMode() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async slackThreadMode => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/slack-thread-mode`, {
        slack_thread_mode: slackThreadMode
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async slackThreadMode_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({
            codesmith_slack_thread_mode: slackThreadMode_0
          });
        }
        return {
          ...old,
          codesmith_slack_thread_mode: slackThreadMode_0
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _slackThreadMode, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update Slack reply setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}

/**
 * Toggle the "[code]smith" check run on PRs entirely. Turning it off means
 * no check is ever created, including the skipped variant carried by PRs
 * [code]smith is not working on.
 */
export function useUpdateCheckRunEnabled() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async enabled => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/check-run-enabled`, {
        enabled
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async enabled_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({
            codesmith_check_run_enabled: enabled_0
          });
        }
        return {
          ...old,
          codesmith_check_run_enabled: enabled_0
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _enabled, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update PR check setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}

/**
 * Org-wide switch for user-written memories. Org-admin gated on the backend;
 * off blocks new and edited memories for every member.
 */
export function useUpdateMemoriesAllowed() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async enabled => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/memories-allowed`, {
        enabled
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async enabled_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({
            codesmith_memories_allowed: enabled_0
          });
        }
        return {
          ...old,
          codesmith_memories_allowed: enabled_0
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _enabled, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update memories setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(["codesmith-memories", activeGithubOrg]);
      queryClient.invalidateQueries("assistantSession", {
        refetchActive: false,
        refetchInactive: false
      });
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}

/**
 * Toggle the org's zero-data-retention opt-out ("Enable highest intelligence
 * models"). Org-admin gated on the backend; opted-out installations default
 * to the highest-intelligence models, which require provider-side data
 * retention. Org-parameterized so non-suspense callers (e.g. the onboarding
 * flow) can pass their own org, mirroring `useCodesmithSettingsForOrg`.
 */
export function useUpdateZdrOptOutForOrg(org) {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== org) {
    t0 = ["codesmith-settings", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== api || $[3] !== org) {
    t1 = async optedOut => {
      const response = await api.put(`/user/github/orgs/${org}/codesmith-settings/zdr-opt-out`, {
        opted_out: optedOut
      });
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = api;
    $[3] = org;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async optedOut_0 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return optimisticSettings({
            codesmith_zdr_opted_out: optedOut_0
          });
        }
        return {
          ...old,
          codesmith_zdr_opted_out: optedOut_0
        };
      });
      return {
        previousSettings
      };
    };
    t3 = (_err, _optedOut, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update model preferences");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== org || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (org) {
        setCachedCodesmithSettings(org, data);
      }
      queryClient.invalidateQueries(queryKey);
    };
    $[9] = org;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
export function useUpdateZdrOptOut() {
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  return useUpdateZdrOptOutForOrg(activeGithubOrg);
}
export type CodeReviewUpdate = {
  mode?: CodeReviewMode;
  trigger?: ReviewTrigger;
  model_mode?: ReviewModelMode;
  publish?: boolean;
};

/**
 * The org's code review mode (disabled, opt_in, opt_out or required), trigger,
 * and GitHub publishing setting. Org-admin gated on the backend.
 */
export function useUpdateCodeReview() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  const applyUpdate = _temp;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async update_0 => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/code-review`, update_0);
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async update_1 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => applyUpdate(old ?? optimisticSettings({}), update_1));
      return {
        previousSettings
      };
    };
    t3 = (_err, _update, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update code review setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(["user-review-settings", activeGithubOrg]);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
function _temp(settings, update) {
  return {
    ...settings,
    ...(update.mode && {
      codesmith_code_review: update.mode
    }),
    ...(update.trigger && {
      codesmith_code_review_trigger: update.trigger
    }),
    ...(update.model_mode && {
      codesmith_code_review_model_mode: update.model_mode
    }),
    ...(update.publish !== undefined && {
      codesmith_code_review_publish: update.publish
    })
  };
}
export type ProofUpdate = {
  mode?: ProofMode;
  trigger?: ProofTrigger;
  max_rounds?: number;
  publish?: ProofPublish;
};
export function useUpdateProof() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  const applyUpdate = _temp2;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async update_0 => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/proof`, update_0);
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async update_1 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => applyUpdate(old ?? optimisticSettings({}), update_1));
      return {
        previousSettings
      };
    };
    t3 = (_err, _update, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update Proof setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(["user-proof-settings", activeGithubOrg]);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
function _temp2(settings, update) {
  return {
    ...settings,
    ...(update.mode && {
      codesmith_proof: update.mode
    }),
    ...(update.trigger && {
      codesmith_proof_trigger: update.trigger
    }),
    ...(update.max_rounds !== undefined && {
      codesmith_proof_max_rounds: update.max_rounds
    }),
    ...(update.publish && {
      codesmith_proof_publish: update.publish
    })
  };
}
export type DreamingUpdate = {
  mode?: DreamingMode;
  trigger?: DreamingTrigger;
};

/**
 * The org's dreaming mode (disabled, opt_in, opt_out or required) and
 * trigger. Org-admin gated on the backend.
 */
export function useUpdateDreaming() {
  const $ = _c(17);
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t0;
  if ($[0] !== activeGithubOrg) {
    t0 = ["codesmith-settings", activeGithubOrg];
    $[0] = activeGithubOrg;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  const applyUpdate = _temp3;
  let t1;
  if ($[2] !== activeGithubOrg || $[3] !== api) {
    t1 = async update_0 => {
      const response = await api.put(`/user/github/orgs/${activeGithubOrg}/codesmith-settings/dreaming`, update_0);
      return CodesmithSettingsSchema.parse(response.data);
    };
    $[2] = activeGithubOrg;
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async update_1 => {
      await queryClient.cancelQueries(queryKey);
      const previousSettings = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => applyUpdate(old ?? optimisticSettings({}), update_1));
      return {
        previousSettings
      };
    };
    t3 = (_err, _update, context) => {
      queryClient.setQueryData(queryKey, context?.previousSettings);
      toast.error("Failed to update dreaming setting");
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeGithubOrg || $[10] !== queryClient || $[11] !== queryKey) {
    t4 = data => {
      if (activeGithubOrg) {
        setCachedCodesmithSettings(activeGithubOrg, data);
      }
      queryClient.invalidateQueries(queryKey);
      queryClient.invalidateQueries(["user-dreaming-settings", activeGithubOrg]);
    };
    $[9] = activeGithubOrg;
    $[10] = queryClient;
    $[11] = queryKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return useMutation(t1, t5);
}
function _temp3(settings, update) {
  return {
    ...settings,
    ...(update.mode && {
      codesmith_dreaming: update.mode
    }),
    ...(update.trigger && {
      codesmith_dreaming_trigger: update.trigger
    })
  };
}